import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdDelete } from 'react-icons/md';
import styled from 'styled-components';

import { HeaderText } from '../../components/Modals';
import { Flex } from '../../components/ui/Flex';
import { ButtonBodyVariant } from '../../components/ui/ButtonVariant';
import { TextArea } from '../../components/ui/TextArea';
import { Palette } from '../../components/utils';
import { TECHNICIAN_NOTE_TEXTAREA_HEIGHT, formatTechnicianNoteMeta } from './technicianNotesUtils';

const TechnicianNotesList = ({
  items = [],
  onAdd,
  onRemove,
  onNoteChange,
  disabled = false
}) => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" gap="8px">
      <Flex justify="space-between" alignItems="center">
        <HeaderText alt style={{ marginBottom: 0 }}>
          {t('Technician Notes')}
        </HeaderText>
        {!disabled && (
          <ButtonBodyVariant
            type="button"
            variant="ghost"
            onClick={onAdd}
            cssOverride="background-color: transparent;"
          >
            <MdAdd size={18} style={{ marginRight: '4px' }} />
            {t('Add Note')}
          </ButtonBodyVariant>
        )}
      </Flex>

      {items.length === 0 && (
        <NoNotesSection>
          {t('No notes added yet. Click "Add Note" to add one.')}
        </NoNotesSection>
      )}

      {items.map((item, index) => (
        <NoteCard key={index}>
          <NoteHeader>
            <NoteTitle>{t('Note')} {index + 1}</NoteTitle>
            {!disabled && (
              <DeleteButton type="button" onClick={() => onRemove(index)}>
                <MdDelete size={20} />
                {t('Remove')}
              </DeleteButton>
            )}
          </NoteHeader>

          {(item.author || item.date) && (
            <NoteMetaText>{formatTechnicianNoteMeta(item.author, item.date)}</NoteMetaText>
          )}

          <TextArea
            height={TECHNICIAN_NOTE_TEXTAREA_HEIGHT}
            rows={4}
            type="text"
            placeholder={t('Add technician notes...')}
            disabled={disabled}
            value={item.note || ''}
            onChange={(e) => onNoteChange(index, e.target.value)}
          />
        </NoteCard>
      ))}

      {!disabled && items.length > 0 && (
        <ButtonBodyVariant
          type="button"
          variant="ghost"
          onClick={onAdd}
          cssOverride="background-color: transparent;"
        >
          <MdAdd size={18} style={{ marginRight: '4px' }} />
          {t('Add Note')}
        </ButtonBodyVariant>
      )}
    </Flex>
  );
};

export default TechnicianNotesList;

const DeleteButton = styled.button`
  padding: 4px;
  display: flex;
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${Palette.ERROR};
  font-weight: 500;
  cursor: pointer;

  & > svg {
    margin-right: 4px;
  }

  &:hover {
    color: #c93a3a;
  }
`;

const NoteCard = styled.div`
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 12px;
  background-color: #f8f9fa;
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const NoteTitle = styled.p`
  margin-bottom: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${Palette.BLACK};
`;

const NoteMetaText = styled.span`
  display: block;
  font-size: 12px;
  color: #8898aa;
  margin-bottom: 8px;
`;

const NoNotesSection = styled.div`
  padding: 16px;
  text-align: center;
  color: #8898aa;
  border: 1px dashed #d8dbe0;
  border-radius: 8px;
`;
